import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ConfirmModal from '@/components/seop/ConfirmModal';
import EmptyState from '@/components/seop/EmptyState';
import { useToast } from '@/contexts/ToastContext';
import { useSeopMonth } from '@/hooks/useSeopMonth';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { seopService } from '@/services/seop.service';
import type { SeopDemand, SeopMonth, SeopProductDetail } from '@/types/seop';
import { fmtMoneyBRL, fmtUnits, parseMoneyBRL } from '@/utils/seopFormat';

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const INPUT =
  'w-full rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-sm';

interface Rascunho {
  demand?: number;
  description?: string;
  family?: string;
  volume?: string;
  unit_value?: number;
  cpv_unit_value?: number;
}

const vazio = { code: '', description: '', family: '', volume: '', pmv: '0', cpv: '0', qty: '0' };

/** PMV − CPV. Hoje o CPV é zero no cadastro inteiro (DT-B11), então margem = PMV. */
const margem = (pmv: number, cpv: number): number => pmv - cpv;

const SeopDemandPage: FC = () => {
  const { month } = useSeopMonth();
  const { showToast } = useToast();
  const [products, setProducts] = useState<SeopProductDetail[]>([]);
  const [demands, setDemands] = useState<SeopDemand[]>([]);
  const [months, setMonths] = useState<SeopMonth[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Rascunho>>({});
  const [busca, setBusca] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [novo, setNovo] = useState(vazio);
  const [excluindo, setExcluindo] = useState<SeopProductDetail | null>(null);

  const mesAtual = months.find((m) => m.index === month) ?? null;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [ps, ms] = await Promise.all([seopService.getProductsDetail(), seopService.getMonths()]);
      setProducts(ps);
      setMonths(ms);
      const mes = ms.find((m) => m.index === month);
      setDemands(mes ? await seopService.getDemands(mes.id) : []);
      setDrafts({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const demandaPorProduto = useMemo(() => {
    const mapa = new Map<string, number>();
    demands.forEach((d) => mapa.set(d.product, d.quantity));
    return mapa;
  }, [demands]);

  const dirty = Object.keys(drafts).length > 0;

  const salvar = useCallback(async () => {
    if (!mesAtual) {
      showToast('Sem mês cadastrado — adicione um mês na tela de Capacidade.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const linhas = Object.entries(drafts)
        .filter(([, d]) => d.demand !== undefined)
        .map(([id, d]) => ({ product: id, month: mesAtual.id, quantity: d.demand as number }));
      if (linhas.length) await seopService.patchDemandBatch(linhas);

      const campos = Object.entries(drafts).filter(
        ([, d]) => Object.keys(d).filter((k) => k !== 'demand').length > 0,
      );
      for (const [id, d] of campos) {
        const { demand: _ignorado, ...resto } = d;
        void _ignorado;
        await seopService.updateProduct(id, resto);
      }
      showToast('Demanda salva.', 'success');
      await fetchData();
    } catch {
      showToast('Erro ao salvar a demanda.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [drafts, mesAtual, showToast, fetchData]);

  useUnsavedChanges(dirty, salvar);

  const editar = (id: string, campo: keyof Rascunho, valor: number | string) =>
    setDrafts((atual) => ({ ...atual, [id]: { ...atual[id], [campo]: valor } }));

  const adicionar = async () => {
    if (!novo.code.trim() || !mesAtual) {
      showToast('Informe ao menos o código do produto.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const criado = await seopService.createProduct({
        code: novo.code.trim(),
        description: novo.description,
        family: novo.family,
        line: novo.family,
        volume: novo.volume,
        unit_value: parseMoneyBRL(novo.pmv),
        cpv_unit_value: parseMoneyBRL(novo.cpv),
      });
      const qtd = Number(novo.qty) || 0;
      if (qtd > 0) {
        await seopService.patchDemandBatch([
          { product: criado.id, month: mesAtual.id, quantity: qtd },
        ]);
      }
      setNovo(vazio);
      showToast('Plano adicionado.', 'success');
      await fetchData();
    } catch {
      showToast('Erro ao adicionar o plano.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const excluir = async () => {
    if (!excluindo) return;
    setIsSaving(true);
    try {
      await seopService.deleteProduct(excluindo.id);
      setExcluindo(null);
      showToast('Plano excluído.', 'success');
      await fetchData();
    } catch {
      showToast('Erro ao excluir o plano.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const linhas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return products;
    return products.filter((p) =>
      `${p.code} ${p.description} ${p.family} ${p.volume}`.toLowerCase().includes(termo),
    );
  }, [products, busca]);

  const totais = useMemo(() => {
    const acumulado = new Map<string, { unidades: number; receita: number }>();
    let unidades = 0;
    let receita = 0;
    for (const p of products) {
      const q = drafts[p.id]?.demand ?? demandaPorProduto.get(p.id) ?? 0;
      const pmv = drafts[p.id]?.unit_value ?? p.unit_value ?? 0;
      const linha = p.family || p.line || '—';
      const atual = acumulado.get(linha) ?? { unidades: 0, receita: 0 };
      atual.unidades += q;
      atual.receita += q * pmv;
      acumulado.set(linha, atual);
      unidades += q;
      receita += q * pmv;
    }
    return { porFamilia: [...acumulado.entries()], unidades, receita };
  }, [products, drafts, demandaPorProduto]);

  if (isLoading) return <div className="p-6 lg:p-10 text-content-muted">Carregando a demanda…</div>;

  if (error) {
    return (
      <div className="p-6 lg:p-10">
        <div className="rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary">
          Não foi possível carregar a demanda. {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm text-content-muted">
          Plano mensal em unidades, PMV, CPV e margem de contribuição.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/balanceamento-linha/demanda/importar?mes=${month}`}
            data-testid="seop-importar-forecast"
            className="rounded-lg border border-theme-border px-3 py-2 text-sm text-content-secondary hover:text-content-primary"
          >
            Importar do forecast
          </Link>
          <button
            type="button"
            data-testid="seop-demanda-salvar"
            onClick={salvar}
            disabled={!dirty || isSaving}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
          >
            {isSaving ? 'Salvando…' : 'Salvar alterações'}
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-lg border border-theme-border bg-surface-secondary px-4 py-3 text-sm text-content-secondary">
        <strong className="text-content-primary">Margem = PMV − CPV.</strong> Hoje o CPV está zerado
        em todo o cadastro, então margem e receita mostram o mesmo número — é o dado que veio da
        planilha, não erro de conta.
      </div>

      <div className="rounded-xl border border-theme-border bg-surface-card p-5">
        <h3 className="mb-3 font-semibold text-content-primary">Novo plano</h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          {(
            [
              ['code', 'Código'],
              ['description', 'Produto'],
              ['family', 'Família'],
              ['volume', 'Volume'],
              ['pmv', 'PMV'],
              ['cpv', 'CPV'],
              ['qty', 'Demanda'],
            ] as const
          ).map(([campo, rotulo]) => (
            <label key={campo} className="text-xs text-content-muted">
              {rotulo}
              <input
                aria-label={`Novo plano — ${rotulo}`}
                value={novo[campo]}
                onChange={(e) => setNovo({ ...novo, [campo]: e.target.value })}
                className={`${INPUT} mt-1`}
              />
            </label>
          ))}
        </div>
        <button
          type="button"
          onClick={adicionar}
          disabled={isSaving}
          data-testid="seop-demanda-adicionar"
          className="mt-3 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
        >
          Adicionar plano
        </button>
      </div>

      <div className="mt-4 rounded-xl border border-theme-border bg-surface-card p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-content-primary">Planos cadastrados</h3>
          <input
            aria-label="Buscar plano"
            placeholder="Buscar código ou produto"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-56 rounded-lg border border-theme-border bg-surface-secondary px-3 py-1.5 text-sm"
          />
        </div>

        {products.length === 0 ? (
          <EmptyState
            icon="inventory_2"
            title="Nenhum produto cadastrado"
            hint="Comece pelos Cadastros — produto, roteiro e parâmetros de lote — e volte aqui para lançar a demanda."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
                  <th className="pb-2 pr-3 text-left">Código</th>
                  <th className="pb-2 pr-3 text-left">Produto</th>
                  <th className="pb-2 pr-3 text-left">Família</th>
                  <th className="pb-2 pr-3 text-left">Volume</th>
                  <th className="pb-2 pr-3 text-right">PMV</th>
                  <th className="pb-2 pr-3 text-right">CPV</th>
                  <th className="pb-2 pr-3 text-right">R$ unit. (margem)</th>
                  <th className="pb-2 pr-3 text-right">Demanda</th>
                  <th className="pb-2 pr-3 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((p) => {
                  const d = drafts[p.id] ?? {};
                  const pmv = d.unit_value ?? p.unit_value ?? 0;
                  const cpv = d.cpv_unit_value ?? p.cpv_unit_value ?? 0;
                  const qtd = d.demand ?? demandaPorProduto.get(p.id) ?? 0;
                  return (
                    <tr key={p.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                      <td className={CELL}>{p.code}</td>
                      <td className={CELL}>
                        <input
                          aria-label={`Produto ${p.code}`}
                          defaultValue={p.description}
                          onBlur={(e) => editar(p.id, 'description', e.target.value)}
                          className={INPUT}
                        />
                      </td>
                      <td className={CELL}>
                        <input
                          aria-label={`Família ${p.code}`}
                          defaultValue={p.family}
                          onBlur={(e) => editar(p.id, 'family', e.target.value)}
                          className={INPUT}
                        />
                      </td>
                      <td className={CELL}>
                        <input
                          aria-label={`Volume ${p.code}`}
                          defaultValue={p.volume}
                          onBlur={(e) => editar(p.id, 'volume', e.target.value)}
                          className={INPUT}
                        />
                      </td>
                      <td className={`${CELL} text-right`}>
                        <input
                          aria-label={`PMV ${p.code}`}
                          defaultValue={fmtMoneyBRL(p.unit_value ?? 0)}
                          onBlur={(e) => editar(p.id, 'unit_value', parseMoneyBRL(e.target.value))}
                          className={`${INPUT} text-right`}
                        />
                      </td>
                      <td className={`${CELL} text-right`}>
                        <input
                          aria-label={`CPV ${p.code}`}
                          defaultValue={fmtMoneyBRL(p.cpv_unit_value ?? 0)}
                          onBlur={(e) => editar(p.id, 'cpv_unit_value', parseMoneyBRL(e.target.value))}
                          className={`${INPUT} text-right`}
                        />
                      </td>
                      <td className={`${CELL} text-right tabular-nums`}>{fmtMoneyBRL(margem(pmv, cpv))}</td>
                      <td className={`${CELL} text-right`}>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          aria-label={`Demanda ${p.code}`}
                          defaultValue={qtd}
                          onBlur={(e) => editar(p.id, 'demand', Math.max(0, Number(e.target.value) || 0))}
                          className={`${INPUT} text-right`}
                        />
                      </td>
                      <td className={CELL}>
                        <button
                          type="button"
                          onClick={() => setExcluindo(p)}
                          data-testid={`seop-demanda-excluir-${p.code}`}
                          className="rounded-lg border border-theme-border px-2 py-1 text-xs text-theme-danger hover:bg-theme-danger/10"
                        >
                          Excluir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-theme-border text-sm font-semibold text-content-primary">
                  <td className="py-2 pr-3" colSpan={6}>
                    Total geral
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtMoneyBRL(totais.receita)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtUnits(totais.unidades)}</td>
                  <td />
                </tr>
                {totais.porFamilia.map(([familia, t]) => (
                  <tr key={familia} className="text-xs text-content-muted">
                    <td className="py-1 pr-3" colSpan={6}>
                      Total {familia}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">{fmtMoneyBRL(t.receita)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{fmtUnits(t.unidades)}</td>
                    <td />
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        open={excluindo !== null}
        title={`Excluir o plano ${excluindo?.code ?? ''}?`}
        isBusy={isSaving}
        confirmLabel="Excluir"
        onConfirm={excluir}
        onCancel={() => setExcluindo(null)}
      >
        <p>
          O produto sai do cadastro junto com <strong>a demanda de todos os meses</strong>, o roteiro
          e os parâmetros de lote ligados a ele.
        </p>
        <p>Esta ação não tem desfazer — salve um cenário antes se quiser poder voltar.</p>
      </ConfirmModal>
    </div>
  );
};

export default SeopDemandPage;
